import React, { useEffect, useRef, useState } from "react";
import { CSSTransitionGroup } from "react-transition-group";
import ReactTooltip from "react-tooltip";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { VoteRadioButton } from "../../Participate/VoteRadioButton";
import { MediaUploader } from "../../../../components/shared/MediaUploader";
import { Select } from "../../../../components/shared";
import { Switch } from "../../../../components/shared/Switch";
import { QUESTION_TYPES, questionTypeList } from "../../../../util/utils";
import QuestionBrowser from "./QuestionBrowser";
import AISuggestionDrawer from './AISuggestionDrawer.jsx';
import "./QuestionEditor.scss";

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  // background: isDragging ? "lightgreen" : "grey",
  // styles we need to apply on draggables
  ...draggableStyle,
});

const getListStyle = (isDraggingOver) => ({
  // background: isDraggingOver ? "lightgrey" : "white",
});

const useClickInside = (ref, callback) => {
  const handleClick = (e) => {
    // console.log("useClickInside", e);
    if (ref.current && ref.current.contains(e.target) && !e.defaultPrevented) {
      callback();
    }
  };
  React.useEffect(() => {
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  });
};

// const useClickOutside = (ref, callback) => {
//   const handleClick = (e) => {
//     if (ref.current && !ref.current.contains(e.target)) {
//       callback();
//     }
//   };
//   React.useEffect(() => {
//     document.addEventListener("click", handleClick);
//     return () => {
//       document.removeEventListener("click", handleClick);
//     };
//   });
// };

export const QuestionEditor = ({
  productType,
  questions,
  onQuestionSetFocus,
  onQuestionFieldChange,
  onQuestionMediaChange,
  onQuestionTypeChange,
  onAddQuestion,
  onSelectTemplateQuestion,
  onInsertQuestion,
  onRemoveQuestion,
  onMoveQuestion,
  onAddOption,
  onRemoveOption,
  onOptionTextChange,
  onOptionMediaChange,
  surveyObjective,
}) => {
  return (
    <div className="question-editor">
      <div className="question-panels">
        <DragDropContext onDragEnd={onMoveQuestion}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
              >
                <CSSTransitionGroup
                  transitionName="fade"
                  transitionEnterTimeout={500}
                  transitionLeaveTimeout={500}
                >
                  {questions.map((q, index) => (
                    <QuestionRow
                      productType={productType}
                      key={q.key}
                      index={index}
                      question={q}
                      questionCount={questions.length}
                      onQuestionSetFocus={onQuestionSetFocus}
                      onQuestionFieldChange={onQuestionFieldChange}
                      onQuestionMediaChange={onQuestionMediaChange}
                      onQuestionTypeChange={onQuestionTypeChange}
                      onInsertQuestion={onInsertQuestion}
                      onRemoveQuestion={onRemoveQuestion}
                      onAddOption={onAddOption}
                      onRemoveOption={onRemoveOption}
                      onOptionTextChange={onOptionTextChange}
                      onOptionMediaChange={onOptionMediaChange}
                      onSelectTemplateQuestion={onSelectTemplateQuestion}
                      surveyObjective={surveyObjective}
                    />
                  ))}
                </CSSTransitionGroup>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      <div
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
      >
        <button className="btn btn-flat" onClick={onAddQuestion}>
          <i className="material-icons">add_circle_outlined</i> Add a question
        </button>
      </div>
    </div>
  );
};

const QuestionRow = ({
  productType,
  index,
  questionCount,
  question,
  onQuestionSetFocus,
  onQuestionFieldChange,
  onQuestionMediaChange,
  onQuestionTypeChange,
  onInsertQuestion,
  onRemoveQuestion,
  onAddOption,
  onRemoveOption,
  onOptionTextChange,
  onOptionMediaChange,
  onSelectTemplateQuestion,
  surveyObjective,
}) => {
  return (
    <Draggable key={question.key} draggableId={question.key} index={index}>
      {(provided, snapshot) => (
        <div
          className="question-row"
          ref={provided.innerRef}
          {...provided.draggableProps}
          // {...provided.dragHandleProps}
          style={getItemStyle(
            snapshot.isDragging,
            provided.draggableProps.style
          )}
        >
          <QuestionPanel
            provided={provided}
            productType={productType}
            index={index}
            questionCount={questionCount}
            question={question}
            onQuestionSetFocus={onQuestionSetFocus}
            onQuestionFieldChange={onQuestionFieldChange}
            onQuestionMediaChange={onQuestionMediaChange}
            onQuestionTypeChange={onQuestionTypeChange}
            onAddOption={onAddOption}
            onRemoveOption={onRemoveOption}
            onOptionTextChange={onOptionTextChange}
            onOptionMediaChange={onOptionMediaChange}
            onSelectTemplateQuestion={onSelectTemplateQuestion}
            surveyObjective={surveyObjective}
          />
          <div className="question-toolbar">
            <button
              className="btn btn-icon"
              onClick={() => onInsertQuestion(index + 1)}
            >
              <i className="material-icons">add_circle_outlined</i>
            </button>
            {questionCount > 1 && (
              <button
                className="btn btn-icon"
                onClick={() => onRemoveQuestion(index)}
              >
                <i className="material-icons">delete_outlined</i>
              </button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

const QuestionPanel = ({
  provided,
  productType,
  question,
  index,
  questionCount,
  onQuestionSetFocus,
  onQuestionFieldChange,
  onQuestionMediaChange,
  onQuestionTypeChange,
  onAddOption,
  onRemoveOption,
  onOptionTextChange,
  onOptionMediaChange,
  onSelectTemplateQuestion,
  surveyObjective,
}) => {
  const [error, setError] = useState("");
  const [isQuestionBrowserOpen, setIsQuestionBrowserOpen] = useState(false);
  const [isAISuggestionDrawerOpen, setIsAISuggestionDrawerOpen] = useState(false);
  // Removed excessive console log that was causing performance issues
  const [questionLength, setQuestionLength] = useState(question.title.length);
  const questionTextRef = useRef();

  const [hasFocus, setHasFocus] = useState(false);
  useEffect(() => {
    setHasFocus(question.hasFocus);
  }, [question.hasFocus]);

  // Set focus to the panel when user click anywhere in the panel
  const clickRef = useRef();
  useClickInside(clickRef, () => {
    // Click inside panel detected
    onQuestionSetFocus(index)();
    // console.log('clickRef', clickRef);
    // questionTextRef.current.focus();
  });

  // const onBlur = () => {
  //   // console.log("QP Blur", q);
  //   // setHasFocus(false);
  // };
  // useClickOutside(clickRef, () => onBlur(question));

  const onQuestionTextChange = (index) => (e) => {
    if (e.target.value) {
      setError("");
    }

    if (e.target.value.length > 501) {
      setError("Question text must be 500 characters or less")
    }

    if (e.target.value.length > 500) {
      e.target.value = e.target.value.slice(0, 500);
    }

    setQuestionLength(e.target.value.length);
    onQuestionFieldChange("title", index)(e);
  };

  const onSelectQuestion = (index) => (question, question_type) => {
    onSelectTemplateQuestion(question, question_type, index);
    setIsQuestionBrowserOpen(false);
  };

  const isQuestionTypeDisabled = question.not_editable?.find(
    (s) => s === "question_type"
  );

  return (
    <div className="question-panel" ref={clickRef}>
      {questionCount > 1 ? (
        <div
          className="drag-handle"
          {...provided.dragHandleProps}
          tabIndex="-1"
        >
          <i className="material-icons">more_horiz</i>
        </div>
      ) : (
        <div className="drag-handle" {...provided.dragHandleProps}></div>
      )}
      <div className="question-number">
        Question {index + 1}
        {hasFocus && (
          <>
            <button
              className="btn btn-flat"
              onClick={(e) => {
                e.nativeEvent.stopImmediatePropagation();
                e.stopPropagation();
                setIsQuestionBrowserOpen(true);
              }}
            >
              <i className="material-icons">library_books</i> Choose a template
              question
            </button>
            
            <button
              className="btn btn-flat"
              onClick={(e) => {
                e.nativeEvent.stopImmediatePropagation();
                e.stopPropagation();
                // console.log('AI Suggestions button clicked');
                setIsAISuggestionDrawerOpen(true);
              }}
            >
              <i className="material-icons ai-suggest-icon">auto_awesome</i> Get AI Suggestions
            </button>
            
            <QuestionBrowser
              isOpen={isQuestionBrowserOpen}
              onClose={() => setIsQuestionBrowserOpen(false)}
              onSelectQuestion={onSelectQuestion(index)}
            />
            <AISuggestionDrawer
               isOpen={isAISuggestionDrawerOpen}
               onClose={() => setIsAISuggestionDrawerOpen(false)}
               surveyObjective={surveyObjective}
               questionType={question.question_type}
               initialPrompt={questionTextRef.current ? questionTextRef.current.value : question.title}
               onSelectSuggestion={(suggestion) => {
                //  console.log('Suggestion selected for question', index, suggestion);

                 // Update React state through the normal flow only
                 const syntheticEvent = { target: { value: suggestion } };
                 setQuestionLength(suggestion.length);
                 setError("");
                 onQuestionFieldChange("title", index)(syntheticEvent);

                 // Close the drawer immediately; textarea is controlled via React
                 setIsAISuggestionDrawerOpen(false);
               }}
             />
          </>
        )}
      </div>
      {!hasFocus ? (
        <PreviewPanel question={question} />
      ) : (
        <div className="edit-mode">
          <div className="row no-gutters">
            <div className="question-text col-8">
              <textarea
                ref={questionTextRef}
                name="title"
                className={`form-control ${error ? "error" : ""}`}
                placeholder="Enter question"
                rows="2"
                onChange={onQuestionTextChange(index)}
                onFocus={(e) => {
                  e.target.select();
                  e.stopPropagation();
                  e.preventDefault();
                }}
                value={question.title}
                onBlur={(e) =>
                  setError(e.target.value ? "" : "Question text is required")
                }
              ></textarea>
              <div className="text-right">
                <small>{questionLength} / 500</small>
              </div>
              {error && <div className="error">{error}</div>}
            </div>
            <div className="question-media col-1">
              <MediaUploader
                id={`q-media-${index}`}
                media={question.media}
                onChange={(media) => onQuestionMediaChange(index, media)}
              />
            </div>
            <div className="question-type col-2">
              {productType === "crowdsource" &&
              index === 0 &&
              question.question_type === "default" ? (
                // <input
                //   type="hidden"
                //   name={`questionType-${index}`}
                //   value={question.question_type}
                // ></input>
                <>
                  {/* Keep the hidden input */}
                  {/* revised by Akmal to show select list */}
                  <input
                    type="hidden"
                    name={`questionType-${index}`}
                    value={question.question_type}
                  ></input>

                  {/* Conditionally render the Select dropdown for visibility */}
                  <span
                    data-tip
                    data-for={
                      isQuestionTypeDisabled
                        ? "questionTypeNotEditableTooltip"
                        : ""
                    }
                  >
                    <Select
                      id={`questionType-${index}`}
                      name={`questionType-${index}`}
                      value={question.question_type}
                      onSelected={(item) =>
                        onQuestionTypeChange(index)({
                          target: { value: item.value },
                        })
                      }
                      itemList={questionTypeList()}
                      disabled={isQuestionTypeDisabled}
                    />
                  </span>
                </>
              ) : (
                <span
                  data-tip
                  data-for={
                    isQuestionTypeDisabled
                      ? "questionTypeNotEditableTooltip"
                      : ""
                  }
                >
                  <Select
                    id={`questionType-${index}`}
                    name={`questionType-${index}`}
                    value={question.question_type}
                    onSelected={(item) =>
                      onQuestionTypeChange(index)({
                        target: { value: item.value },
                      })
                    }
                    itemList={questionTypeList()}
                    disabled={isQuestionTypeDisabled}
                  />
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-8">
              {question.question_type !== "default" && (
                <ResponseOptionEditor
                  questionIndex={index}
                  disabled={isQuestionTypeDisabled}
                  options={question.options}
                  options_editable={
                    QUESTION_TYPES[question.question_type].options_editable
                  }
                  onAddOption={(e) => {
                    onAddOption(index);
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onRemoveOption={(optionIndex) =>
                    onRemoveOption(index, optionIndex)
                  }
                  onOptionTextChange={(optionIndex, value) =>
                    onOptionTextChange(index, optionIndex, value)
                  }
                  onOptionMediaChange={(optionIndex, media) =>
                    onOptionMediaChange(index, optionIndex, media)
                  }
                />
              )}
            </div>
            <div className="col-4">
              <AdditionalSettings
                index={index}
                question={question}
                onQuestionFieldChange={onQuestionFieldChange}
                disabled={isQuestionTypeDisabled}
              />
            </div>
          </div>
          <Tooltip enable={isQuestionTypeDisabled} />
        </div>
      )}
    </div>
  );
};

const PreviewPanel = ({ question }) => {
  return (
    <div className="preview-mode">
      <div className="question-title">{question.title || "Question"}</div>
      {/* {question.question_type === "default" && (
        <input
          type="text"
          className="form-control"
          disabled
          placeholder="Add a response"
        />
      )} */}
      {question.question_type !== "default" && (
        <div className="option-editor">
          {question.options &&
            question.options.map((option, index) => (
              <div
                key={`option-${question.id}-${index}`}
                className="option-row"
              >
                <div className="mr-2">
                  <VoteRadioButton />
                </div>
                <div className="readonly-option">{option.name}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const ResponseOptionEditor = ({
  questionIndex,
  disabled,
  options,
  options_editable,
  onAddOption,
  onRemoveOption,
  onOptionTextChange,
  onOptionMediaChange,
}) => {
  return (
    <div className="option-editor">
      {options &&
        options_editable &&
        options.map((option, index) => (
          <div key={`option-${questionIndex}-${index}`} className="option-row">
            {/* <div className="dnd-icon">
              {options_editable && <i className="material-icons">more_vert</i>}
            </div> */}
            <div className="mr-4">{/* <VoteRadioButton /> */}</div>
            {!options_editable && (
              <div className="readonly-option">{option.name}</div>
            )}
            {options_editable && (
              <div className="row no-gutters">
                <span
                  data-tip
                  data-for={disabled ? "questionTypeNotEditableTooltip" : ""}
                >
                {/* style was commented by akmal */}
                  <input
                    type="text"
                    className="form-control"
                    disabled={disabled}
                    value={option.name}
                    // style={{ width: "30em" }} // style was commented by akmal
                    maxLength={50}
                    onFocus={(e) => {
                      e.target.select();
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onChange={(e) => onOptionTextChange(index, e.target.value)}
                  />
                </span>
                <div className="option-media">
                  <MediaUploader
                    id={`q-option-media-${index}`}
                    media={option.media}
                    onChange={(media) => onOptionMediaChange(index, media)}
                    disabled={disabled}
                    disabledTooltip="This cannot be changed because there has been some participation"
                  />
                </div>
                <div className="option-actions">
                  {(options.length > 2 || index > 1) && !disabled && (
                    <button
                      className="btn btn-icon btn-delete-option"
                      onClick={() => onRemoveOption(index)}
                    >
                      <i className="material-icons">delete_outlined</i>
                    </button>
                  )}
                </div>
              </div>
            )}
            <Tooltip enable={disabled} />
          </div>
        ))}
      {options_editable && !disabled && (
        <button className="btn btn-flat ml-0" onClick={onAddOption}>
          <i className="material-icons">add_circle_outlined</i>Add Option
        </button>
      )}
    </div>
  );
};

const Tooltip = ({ enable }) => {
  return (
    <ReactTooltip id="questionTypeNotEditableTooltip" disable={!enable}>
      This cannot be changed because there has been some participation
    </ReactTooltip>
  );
};

const AdditionalSettings = ({
  index,
  question,
  onQuestionFieldChange,
  disabled,
}) => {
  return (
    <div className="addl-settings">
      {question.question_type === "multiple" && (
        <Switch
          className="setting"
          id={`randomizeChoiceOrder-${index}`}
          checked={!!question.randomize_choice_order}
          text="Randomize choice order"
          disabled={disabled}
          disabledTooltip="This cannot be changed because there has been some participation"
          onChange={(value) => {
            onQuestionFieldChange(
              "randomize_choice_order",
              index
            )({ target: { value: value } });
          }}
        />
      )}
      {question.question_type === "default" && index > 0 && (
        <>
          {[
            { label: "Like/Dislike", value: "like-dislike" },
            { label: "Star Rating", value: "star-rating" },
          ].map((v) => (
            <span
              key={`voting_mode_${v.value}_${index}`}
              data-tip
              data-for={disabled ? "questionTypeNotEditableTooltip" : ""}
            >
              <label
                htmlFor={`voting_mode_${v.value}_${index}`}
                className={`${disabled ? "disabled" : ""}`}
              >
                <input
                  type="radio"
                  id={`voting_mode_${v.value}_${index}`}
                  value={v.value}
                  checked={question.voting_mode === v.value}
                  disabled={disabled}
                  onChange={(e) =>
                    onQuestionFieldChange("voting_mode", index)(e)
                  }
                />
                {v.label}
              </label>
            </span>
          ))}
        </>
      )}
      {question.question_type !== "default" && (
        <Switch
          className="setting"
          id={`enableComments-${index}`}
          checked={!!question.enable_comments}
          text="Enable comments"
          onChange={(value) =>
            onQuestionFieldChange(
              "enable_comments",
              index
            )({ target: { value: value } })
          }
        />
      )}
      <Tooltip enabled={disabled} />
    </div>
  );
};

export default QuestionEditor;
